import { BuildOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';

import {
  NAVIGATOR_TARGET,
  type NavigatorSelectItem,
} from '@/features/Commons/NavigatorFloat/types/navigatorTypes';
import service from '@/services';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import {
  buildTaxonomyFeedbackPath,
  buildTaxonomyTopicPath,
} from '../utils/routes';
import taxonomyUtils from '../utils/taxonomy';
import useTaxonomyTopicIcon from './useTaxonomyTopicIcon';

const useFlattenTaxonomyTopicListQuery = () => {
  const getIcon = useTaxonomyTopicIcon();

  const fetcher = async () => {
    const taxonomies = await service.taxonomy.fetchTaxonomyList(
      getCurrWorkspaceAlias(),
    );
    const result: NavigatorSelectItem[] = [];

    for (const taxonomy of taxonomies) {
      const topics = await service.taxonomy.fetchTaxonomyTopicList(
        taxonomy.alias,
        '',
      );

      const topicNodes: NavigatorSelectItem[] = topics.map((topic) => ({
        target: NAVIGATOR_TARGET.TOPIC,
        alias: topic.alias,
        name: topic.name,
        icon: getIcon(topic.category),
        path: taxonomyUtils
          .getTopicPath({
            ...topic,
            taxonomy_name: taxonomy.title,
          })
          .map((t) => t.label)
          .join(' / '),
        href: buildTaxonomyTopicPath(taxonomy.alias, topic.alias),
      }));

      result.push({
        target: NAVIGATOR_TARGET.TAXONOMY,
        alias: taxonomy.alias,
        name: taxonomy.title,
        icon: <BuildOutlined style={{ fontSize: 20 }} />,
        href: buildTaxonomyFeedbackPath(taxonomy.alias),
      });

      result.push(...topicNodes);
    }

    return result;
  };

  return useQuery({
    queryKey: ['taxonomy-topic-list'],
    queryFn: fetcher,
    staleTime: 1000 * 60 * 5,
  });
};

export default useFlattenTaxonomyTopicListQuery;
